import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import Div100vh from 'react-div-100vh';
import { Check, X } from 'lucide-react';
import Popup from '../Popup';
import { enableScroll } from '../../utils';

/**
 * App-level success confirmation shown the moment a rewarded ad CLOSES after
 * a granted reward. The credit + cooldown clear already happened silently
 * (behind the fullscreen ad); this is the visible "it worked" the user needs.
 *
 * Why a modal and not the old snackbar: the snackbar fired while the ad still
 * covered the page (never seen), and even on close a transient toast was too
 * easy to miss — users reported "I closed the ad and nothing happened, I
 * can't tell it worked." A centered confirmation with a clear next action
 * removes all ambiguity.
 */

const messages = defineMessages({
  title: {
    id: 'rewardedSuccess.title',
    defaultMessage: 'Free vote unlocked!',
  },
  bodyNamed: {
    id: 'rewardedSuccess.bodyNamed',
    defaultMessage: 'Your next free vote for {petName} is ready to use.',
  },
  body: {
    id: 'rewardedSuccess.body',
    defaultMessage: 'Your next free vote is ready to use.',
  },
  primary: {
    id: 'rewardedSuccess.primary',
    defaultMessage: 'Vote now',
  },
});

const C = {
  primary: '#50B772',
  primaryDark: '#3D9B5C',
  text: '#0F172A',
  muted: '#64748B',
  closeIcon: '#94A3B8',
};

// Auto-dismiss as a fallback so the modal never strands the user, but long
// enough to read + act.
const AUTO_DISMISS_MS = 8000;

const RewardedSuccessModal = ({ open, petName, onClose }) => {
  const { formatMessage } = useIntl();
  const safePetName = (petName || '').trim();

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => { onClose(); enableScroll(); }, AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [open, onClose]);

  const dismiss = () => { onClose(); enableScroll(); };

  return (
    <Popup
      open={open}
      position="centerCenter"
      closeOnEsc
      closeOnOverlay
      onClose={dismiss}
      paperClassname="rewarded-success-paper"
      wrapperClassname="modal-wrapper fullHeight vote-wrapper"
      overlayStyle={{
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 99, backgroundColor: 'rgba(15,23,42,0.55)',
      }}
      contentStyle={{
        backgroundColor: 'transparent', zIndex: 2000, position: 'relative', maxWidth: 360, padding: 0,
      }}
    >
      <Div100vh style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 14 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 340, damping: 26 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="rewarded-success-title"
          style={{
            position: 'relative',
            background: 'radial-gradient(ellipse 220px 120px at 50% 0%, rgba(80,183,114,0.10) 0%, transparent 70%), linear-gradient(180deg, #FFFFFF 0%, #FBFDFC 100%)',
            borderRadius: 24,
            padding: '40px 28px 28px',
            width: '100%',
            maxWidth: 360,
            boxShadow: '0 24px 64px rgba(15,23,42,0.24), 0 4px 14px rgba(15,23,42,0.06)',
            textAlign: 'center',
            overflow: 'hidden',
          }}
        >
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            style={{
              position: 'absolute', top: 12, right: 12, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 0, padding: 0, cursor: 'pointer', color: C.closeIcon, borderRadius: 10,
            }}
          >
            <X size={18} strokeWidth={2.5} />
          </button>

          {/* Check medallion — the unmistakable "done" signal */}
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              type: 'spring', stiffness: 420, damping: 16, delay: 0.05,
            }}
            aria-hidden="true"
            style={{
              width: 76, height: 76, margin: '0 auto 20px', borderRadius: '50%', background: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF', boxShadow: '0 10px 24px rgba(80,183,114,0.45)',
            }}
          >
            <Check size={40} strokeWidth={3.5} />
          </motion.div>

          <motion.h2
            id="rewarded-success-title"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.22 }}
            style={{
              fontSize: 23, fontWeight: 800, color: C.text, margin: '0 0 10px', letterSpacing: '-0.018em', lineHeight: 1.2,
            }}
          >
            {formatMessage(messages.title)}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.22 }}
            style={{
              fontSize: 15, fontWeight: 600, color: '#334155', lineHeight: 1.45, margin: '0 auto 22px', maxWidth: 290,
            }}
          >
            {safePetName
              ? formatMessage(messages.bodyNamed, { petName: safePetName })
              : formatMessage(messages.body)}
          </motion.p>

          <motion.button
            type="button"
            onClick={dismiss}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.24, duration: 0.22 }}
            whileHover={{ y: -1 }}
            whileTap={{ y: 1 }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 54, padding: '0 20px', borderRadius: 16, background: C.primary, border: 0, borderBottom: `4px solid ${C.primaryDark}`, color: '#FFFFFF', fontSize: 16, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.015em', boxShadow: '0 10px 22px rgba(80,183,114,0.32)',
            }}
          >
            {formatMessage(messages.primary)}
          </motion.button>
        </motion.div>
      </Div100vh>
    </Popup>
  );
};

RewardedSuccessModal.propTypes = {
  open: PropTypes.bool.isRequired,
  petName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

RewardedSuccessModal.defaultProps = {
  petName: '',
};

export default RewardedSuccessModal;
